import { HttpClient } from '@angular/common/http';
import { Directive, ElementRef, OnDestroy, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { CountriesService, CitiesService, ProvincesService, RegionsService, DropdownsService } from 'app/services';
import { environment } from 'environments/environment';
import { find, forEach } from 'lodash';
import { forkJoin, Observable, of } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';


declare var google: any;

@Directive({
    selector: '[googlePlaces]'
})
export class GooglePlacesDirective implements OnInit, OnDestroy {

    @Input() address: FormControl = new FormControl('');
    @Input() country: FormControl;
    @Input() region: FormControl;
    @Input() city: FormControl;
    @Input() province: FormControl;
    @Input() postcode: FormControl;
    @Input() street: FormControl;
    // tslint:disable-next-line: variable-name
    @Input() street_number: FormControl;
    @Input() latitude: FormControl;
    @Input() longitude: FormControl;
    @Input() usePublicEndpoints: boolean = false;

    @Output() selectAddress: EventEmitter<any> = new EventEmitter();
    // true while the geo lookups triggered by a place selection are patching the bound controls
    @Output() geoPatching: EventEmitter<boolean> = new EventEmitter();
    // Emits matched city docs so the parent can populate the dropdown without another API round-trip.
    @Output() citiesResolved: EventEmitter<any[]> = new EventEmitter();
    /** Emits resolved geo docs so parent dropdowns can show labels without extra API calls. */
    @Output() geoResolved: EventEmitter<{ regions: any[]; provinces: any[]; cities: any[] }> = new EventEmitter();
    private element: HTMLInputElement;
    private placeChangedListener: any;
    private loadTimer: number | null = null;
    private destroyed = false;

    filter: any;
    countryData: any;
    provinceData: any;
    cityData: any;
    regionData: any;

    constructor(
        private elRef: ElementRef,
        private _dropdownService: DropdownsService,
        private _countriesService: CountriesService,
        private _citiesService: CitiesService,
        private _provincesService: ProvincesService,
        private _regionService: RegionsService,
        private http: HttpClient
    ) {
        this.filter = {
            options: { page: 1, limit: 30, sort: {} }
        };
        this.element = this.elRef.nativeElement;

    }

    private normalizeToDocsFormat(data: any): any {
        return Array.isArray(data) ? { docs: data } : data;
    }

    /** Signup suppresses geo valueChanges during patch; CRM modules rely on them for dropdown cascades. */
    private shouldSuppressGeoEvents(): boolean {
        return this.usePublicEndpoints;
    }

    private setGeoControl(control: FormControl | undefined, value: any): void {
        if (!control) {
            return;
        }
        if (this.shouldSuppressGeoEvents()) {
            control.setValue(value, { emitEvent: false });
        } else {
            control.setValue(value);
        }
    }

    ngOnInit(): void {
        this.initAutocomplete();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        if (this.loadTimer != null) {
            window.clearInterval(this.loadTimer);
            this.loadTimer = null;
        }
        if (this.placeChangedListener && typeof google !== 'undefined') {
            google.maps.event.removeListener(this.placeChangedListener);
        }
    }

    private async initAutocomplete(): Promise<void> {
        const places = await this.loadPlacesLibrary();
        if (this.destroyed || !places?.Autocomplete) {
            return;
        }

        const autocomplete = new places.Autocomplete(this.element);
        this.placeChangedListener = google.maps.event.addListener(autocomplete, 'place_changed', () => {
            this.handlePlaceChanged(autocomplete);
        });
    }

    /**
     * Maps is loaded with loading=async in index.html, so google.maps.places
     * may not exist yet when this directive's ngOnInit runs (especially in a
     * newly opened Material dialog).
     */
    private loadPlacesLibrary(): Promise<any> {
        if (typeof google !== 'undefined' && google.maps?.places?.Autocomplete) {
            return Promise.resolve(google.maps.places);
        }
        if (typeof google !== 'undefined' && typeof google.maps?.importLibrary === 'function') {
            return google.maps.importLibrary('places');
        }

        return new Promise((resolve) => {
            const started = Date.now();
            this.loadTimer = window.setInterval(() => {
                if (typeof google !== 'undefined' && google.maps?.places?.Autocomplete) {
                    window.clearInterval(this.loadTimer);
                    this.loadTimer = null;
                    resolve(google.maps.places);
                    return;
                }
                if (typeof google !== 'undefined' && typeof google.maps?.importLibrary === 'function') {
                    window.clearInterval(this.loadTimer);
                    this.loadTimer = null;
                    google.maps.importLibrary('places').then(resolve).catch(() => resolve(null));
                    return;
                }
                if (Date.now() - started > 10000) {
                    window.clearInterval(this.loadTimer);
                    this.loadTimer = null;
                    resolve(null);
                }
            }, 100);
        });
    }

    private handlePlaceChanged(autocomplete: any): void {
        this.selectAddress.emit(autocomplete.getPlace());
        this.geoPatching.emit(true);

        this.getAddress(autocomplete.getPlace())
            .then((resolvedAddress) => this.patchAddressFields(resolvedAddress))
            .catch(() => this.geoPatching.emit(false));
    }

    private patchAddressFields(address: any): void {
        let countryName: any;
        let cityName: any;
        let ProvinceName: any;
        let postalCode: any;
        let streetName: any;
        let streetNumber: any;
        let regionName: any;

        if (this.country) { this.setGeoControl(this.country, null); }
        if (this.region) { this.setGeoControl(this.region, null); }
        if (this.city) { this.setGeoControl(this.city, null); }
        if (this.province) { this.setGeoControl(this.province, null); }
        if (this.postcode) { this.postcode.setValue(null); }
        if (this.street) { this.street.setValue(null); }
        if (this.street_number) { this.street_number.setValue(null); }
        if (this.latitude) { this.latitude.setValue(null); }
        if (this.longitude) { this.longitude.setValue(null); }

        postalCode = find(address.address_components, (component: any) => component.types.indexOf('postal_code') > -1);
        streetName = find(address.address_components, (component: any) => component.types.indexOf('route') > -1);
        streetNumber = find(address.address_components, (component: any) => component.types.indexOf('street_number') > -1);
        if (postalCode && this.postcode) { this.postcode.setValue(postalCode.long_name); }
        if (streetName && this.street) { this.street.setValue(streetName.long_name); }
        if (streetNumber && this.street_number) { this.street_number.setValue(streetNumber.long_name); }
        if (address.formatted_address && this.address) { this.address.setValue(address.formatted_address); }
        countryName = find(address.address_components, (component: any) => component.types.indexOf('country') > -1);
        cityName = find(address.address_components, (component: any) => component.types.indexOf('locality') > -1 || component.types.indexOf('administrative_area_level_3') > -1);
        ProvinceName = find(address.address_components, (component: any) => component.types.indexOf('administrative_area_level_2') > -1);
        regionName = find(address.address_components, (component: any) => component.types.indexOf('administrative_area_level_1') > -1);

        // TODO: if province and country is same than check either province and region are correct or need to assign region value to province and assign correct valu to region
        if (countryName?.long_name == 'Netherlands' && (regionName?.long_name == 'Zuid-Holland' || regionName?.long_name == 'South Holland')) {
            ProvinceName.long_name = regionName.long_name;
            ProvinceName.short_name = regionName.short_name;
            regionName.long_name = 'West Holland';
            regionName.short_name = 'WH';
        }
        if (this.longitude && address.geometry?.location) {
            if (typeof address.geometry.location.lng == 'function') {
                this.longitude.setValue(address.geometry.location.lng());
            } else {
                this.longitude.setValue(address.geometry.location.lng);
            }
        }
        if (this.latitude && address.geometry?.location) {
            if (typeof address.geometry.location.lat == 'function') {
                this.latitude.setValue(address.geometry.location.lat());
            } else {
                this.latitude.setValue(address.geometry.location.lat);
            }
        }

        const countryLookups: Observable<any>[] = [];

        if (countryName && this.country) {
            const countryObs$ = this.usePublicEndpoints
                ? this._dropdownService.getAllCountries(countryName.long_name).pipe(map((d: any) => this.normalizeToDocsFormat(d)))
                : this._dropdownService.getCountries(countryName.long_name);
            countryLookups.push(countryObs$.pipe(tap((data: any) => {
                if (data.docs[0]) {
                    this.countryData = data.docs[0];
                    this.setGeoControl(this.country, this.countryData.key);
                }
            })));
        }

        const countryStep$ = countryLookups.length ? forkJoin(countryLookups) : of(null);

        countryStep$
            .pipe(
                switchMap(() => this.resolveRegionAndProvince(regionName, ProvinceName)),
                switchMap(() => (cityName && this.city ? this.loadCityMatch(cityName) : of(null))),
                finalize(() => this.geoPatching.emit(false))
            )
            .subscribe((data: any) => {
                const cities = data?.docs || [];
                if (data?.docs?.[0]) {
                    this.applyCitySelection(data, cityName);
                }

                if (this.usePublicEndpoints) {
                    if (cities.length) {
                        this.citiesResolved.emit(cities);
                    }
                    this.geoResolved.emit({
                        regions: this.regionData ? [this.regionData] : [],
                        provinces: this.provinceData ? [this.provinceData] : [],
                        cities,
                    });
                    // Re-emit country so signup validators / phone sync run after silent patch.
                    if (this.country?.value != null) {
                        this.country.setValue(this.country.value, { emitEvent: true });
                    }
                }
            });
    }

    /** Resolve region then province (province query needs the region key). */
    private resolveRegionAndProvince(regionName: any, ProvinceName: any): Observable<any> {
        const countryKey = this.country?.value;

        const regionStep$ = (regionName && this.region)
            ? (this.usePublicEndpoints
                ? this._dropdownService.getRegions(regionName.long_name, countryKey || undefined, true)
                    .pipe(map((d: any) => this.normalizeToDocsFormat(d)))
                : this._dropdownService.getRegions(regionName.long_name, countryKey || undefined)
            ).pipe(tap((data: any) => {
                if (data.docs[0] && this.region) {
                    this.regionData = data.docs[0];
                    this.setGeoControl(this.region, this.regionData.key);
                }
            }))
            : of(null);

        return regionStep$.pipe(
            switchMap(() => {
                if (!ProvinceName || !this.province) {
                    return of(null);
                }
                return (this.usePublicEndpoints
                    ? this._dropdownService.getProvinces(
                        ProvinceName.long_name,
                        countryKey || undefined,
                        this.region?.value || undefined,
                        true
                    ).pipe(map((d: any) => this.normalizeToDocsFormat(d)))
                    : this._dropdownService.getProvinces(
                        ProvinceName.long_name,
                        countryKey || undefined,
                        this.region?.value || undefined
                    )
                ).pipe(tap((data: any) => {
                    if (data.docs[0]) {
                        this.provinceData = data.docs[0];
                        this.setGeoControl(this.province, this.provinceData.key);
                    }
                }));
            })
        );
    }

    private loadCityMatch(cityName: any): Observable<any> {
        if (this.usePublicEndpoints) {
            return this._dropdownService.getAllCities(
                cityName.long_name,
                this.province?.value || undefined
            ).pipe(map((d: any) => this.normalizeToDocsFormat(d)));
        }
        return this._dropdownService.getCities(
            cityName.long_name,
            false,
            this.region?.value,
            this.province?.value
        );
    }

    private applyCitySelection(data: any, cityName: any): void {
        this.cityData = data.docs[0];
        if (cityName.long_name === 'Ronda') {
            forEach(data.docs, (content: any) => {
                if ((content.value.en === cityName.long_name || content.value.es_AR === cityName.long_name)
                    && content.country === this.country.value) {
                    this.setGeoControl(this.city, content.key);
                }
            });
        } else {
            this.setGeoControl(this.city, this.cityData.key);
        }
        if (this.region && !this.region.value) {
            this.setGeoControl(this.region, this.cityData.region);
        }
        if (this.province && !this.province.value) {
            this.setGeoControl(this.province, this.cityData.province);
        }
    }

    getAddress(address) {
        // Autocomplete places already include address_components — skip the extra geocode round-trip.
        if (address?.address_components?.length) {
            return Promise.resolve(address);
        }
        return new Promise((resolve) => {
            let query = '';
            if (address.formatted_address) {
                query = address.formatted_address;
            } else if (address.name) {
                query = address.name;
            }
            if (query) {
                this.http.get('https://maps.googleapis.com/maps/api/geocode/json?address=' +
                    query + '&key=' + environment.googleMapApiKey)
                    .subscribe({
                        next: async (coord_results: any) => {
                            resolve(coord_results?.results?.[0] ?? address);
                        },
                        error: () => resolve(address)
                    });

            } else {
                resolve(address);
            }
        });
    }
}
